import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  getFirestore,
  doc,
  updateDoc,
  onSnapshot,
  getDoc,
  setDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Stack,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  formatTimestamp,
  toEnglishNumber,
  validateAndFormatPhoneNumber,
} from "../../Funcs/Miscellaneous";
import {
  BellFillIcon,
  BellIcon,
  ImageIcon,
  MedicalFileIcon,
  MusicPlayerIcon,
  SendIcon,
} from "../../Utils/SVGs";

// Import the notification sound
import notificationSound from "../../Assets/bubblepop.mp3";
import { BasicColor, GreenContrast } from "../../Utils/constants";
import ChatCameraCaptureButton from "./ChatCaptureImageBtn";
import LoadingDots from "./LoadingDots";
import {
  simpleBase62Decode,
  simpleBase62DecodeString,
} from "../../Funcs/Encryption";
import { compressImage } from "../../Funcs/Miscellaneous";
import WhatsappBtn from "./WhatsappBtn";

const ChatApp = ({ isDoctor, mrn, pName, pPhone, URL }) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const id = isDoctor ? mrn : simpleBase62Decode(params.get("mrn"));
  const ptName = isDoctor ? pName : simpleBase62DecodeString(params.get("n"));
  const ptPhone = isDoctor
    ? pPhone
    : `0${simpleBase62DecodeString(params.get("p"))}`;
  const theUrl = isDoctor ? URL : window.location.href;
  // const id = mrn;
  const db = getFirestore();
  const storage = getStorage();

  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null); // State for selected file
  const [modalShow, setModalShow] = useState(false);
  const [newMessage, setNewMessage] = useState({
    fasting: "",
    postPrandial: "",
    random: "",
    note: "",
    date: new Date().toISOString().slice(0, 10),
  });
  const [messageSent, setMessageSent] = useState(false);

  const messagesEndRef = useRef(null);
  const previousMessagesRef = useRef([]);
  const audio = useRef(new Audio(notificationSound)); // Audio instance for notifications
  const [capturedImage, setCapturedImage] = useState(null); // State to hold the captured image
  const [showFileInput, setshowFileInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [documentData, setDocumentData] = useState({});
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    // console.log("currentUrl", currentUrl);
    if (!id) {
      alert("You must enter mrn\n يجب إدخال رقم الملف");
      return;
    }

    const docRef = doc(db, "chats", id);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const newMessages = docSnap.data().messages || [];
        setMessages(newMessages);
        setDocumentData(docSnap.data());
        // Play sound for new message
        if (newMessages.length > previousMessagesRef.current.length) {
          audio.current.play();
        }

        previousMessagesRef.current = newMessages;
      } else {
        console.log("No such document!");
      }
    });

    return () => unsubscribe();
  }, [id, db]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const uploadFile = async () => {
    let fileToUpload = file;

    // If there is a captured image, convert it to a Blob
    if (capturedImage && !file) {
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      const fileName = `captured_${Date.now()}.jpg`; // Name for the captured image
      fileToUpload = new File([blob], fileName, { type: "image/jpeg" });
    }

    if (!fileToUpload) return null;

    const fileRef = ref(
      storage,
      `${id}/chats/${Date.now()}_${fileToUpload.name}`
    );
    const uploadTask = uploadBytesResumable(fileRef, fileToUpload);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        null,
        (error) => {
          console.error("Error uploading file:", error);
          reject(error);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const sendMessage = async (newMessageData) => {
    if (!id) {
      alert("ID not provided in the URL.");
      return;
    }

    const docRef = doc(db, "chats", id);

    let fileURL = null;
    if (file || capturedImage) {
      fileURL = await uploadFile(); // Upload file or captured image
      setFile(null); // Clear file input
      setCapturedImage(null); // Clear captured image
    }

    const newMessage = {
      ...newMessageData,
      sender: isDoctor ? "doctor" : "patient",
      timestamp: new Date().toISOString(),
      fileURL, // Attach the file URL if a file or captured image was uploaded
    };

    try {
      // Check if the document exists
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        // Update the existing document
        await updateDoc(docRef, {
          messages: [...docSnap.data().messages, newMessage],
          isOpen: false,
          isFavourite: true,
          ptName: ptName,
          ptPhone: ptPhone,
          chatUrl: theUrl,
        });
      } else {
        // Create a new document with the first message
        await setDoc(docRef, {
          isFavourite: true,
          isOpen: false,
          messages: [newMessage],
          ptName: ptName,
          ptPhone: ptPhone,
          chatUrl: theUrl,
        });
      }

      setMessageSent(true);
      setIsLoading(false);
      setTimeout(() => setMessageSent(false), 3000);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleDoctorSend = async () => {
    setIsLoading(true);
    if (!message.trim() && !file && !capturedImage) return;
    await sendMessage({ note: message });
    setMessage("");
  };

  const handlePatientSend = async () => {
    setIsLoading(true);
    await sendMessage(newMessage);
    setModalShow(false);
    setNewMessage({
      fasting: "",
      postPrandial: "",
      random: "",
      note: "",
      date: new Date().toISOString().slice(0, 10),
    });
  };

  // const handleFileChange = (e) => {
  //   setFile(e.target.files[0]);
  //   setCapturedImage(null);
  // };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = file.type;

    // Check if file is an image
    if (fileType.startsWith("image/")) {
      try {
        const compressedBlob = await compressImage(file, 100 * 1024); // Compress to 250 KB
        const compressedFile = new File([compressedBlob], file.name, {
          type: file.type,
        });
        setFile(compressedFile);
        setCapturedImage(URL.createObjectURL(compressedBlob)); // Set captured image to the state
      } catch (error) {
        console.error("Error compressing image:", error);
        setFile(file); // In case of error, use the original file
      }
    } else {
      // If it's not an image, just set it as the file
      setFile(file);
      setCapturedImage(null);
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="text-center">
        {isDoctor ? pName : "Patient Chat"} - {id}
      </h2>
      <div
        style={{
          maxHeight: "400px",
          overflowY: "auto",
          border: "1px solid #ccc",
          borderRadius: "10px",
          padding: "10px",
          marginBottom: "20px",
          backgroundColor: "white",
        }}
      >
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              textAlign:
                msg.sender === (isDoctor ? "doctor" : "patient")
                  ? "right"
                  : "left",
              margin: "10px 0",
            }}
          >
            <div
              style={{
                display: "inline-block",
                padding: "10px",
                borderRadius: "10px",
                background:
                  msg.sender === (isDoctor ? "doctor" : "patient")
                    ? "#d1f7c4"
                    : "#f1f1f1",
                maxWidth: "80%",
              }}
            >
              {msg.note && <p>{msg.note}</p>}
              {msg.fasting && <p>Fasting: {msg.fasting}</p>}
              {msg.postPrandial && <p>Post Prandial: {msg.postPrandial}</p>}
              {msg.random && <p>Random: {msg.random}</p>}

              {msg.fileURL && (
                <div>
                  {/* Display a loading placeholder while checking the file type */}
                  <span id={`placeholder-${index}`}>Checking file...</span>
                  <img
                    src={msg.fileURL}
                    alt="Uploaded file"
                    style={{
                      maxWidth: "100px",
                      maxHeight: "100px",
                      objectFit: "cover",
                      borderRadius: "5px",
                      cursor: "pointer",
                      display: "none", // Initially hidden until confirmed as an image
                    }}
                    onClick={() =>
                      window.open(msg.fileURL, "_blank", "noopener,noreferrer")
                    }
                    onLoad={(e) => {
                      document.getElementById(
                        `placeholder-${index}`
                      ).style.display = "none";
                      e.target.style.display = "block";
                    }}
                    onError={(e) => {
                      // Fallback for non-image files: Force download
                      // Fallback for non-image files
                      const placeholder = document.getElementById(
                        `placeholder-${index}`
                      );
                      placeholder.textContent = "Download File";
                      placeholder.style.color = "blue";
                      placeholder.style.cursor = "pointer";
                      placeholder.onclick = () =>
                        window.open(msg.fileURL, "_blank");
                      e.target.remove();
                    }}
                  />
                </div>
              )}

              <small className="text-muted">
                {formatTimestamp(msg.timestamp)}{" "}
              </small>
              <small style={{ color: "#0490fb", fontWeight: "bolder" }}>
                {messages.length - 1 === index
                  ? documentData.isOpen
                    ? "✓✓"
                    : "✓"
                  : "✓✓"}
              </small>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      {isDoctor ? (
        <Row>
          <div direction="horizontal" gap={1}>
            <Stack direction="horizontal">
              {!isIOS && (
                <ChatCameraCaptureButton
                  onCapture={(image) => {
                    setCapturedImage(image); // Set captured image
                    setFile(null); // Clear any selected file
                  }}
                />
              )}
              {!showFileInput && (
                <Button
                  style={{ backgroundColor: BasicColor }}
                  onClick={() => setshowFileInput(true)}
                >
                  {ImageIcon("", 20)}
                </Button>
              )}
            </Stack>
            {showFileInput && (
              <Form.Group className="mb-3">
                <Form.Label>Attach File</Form.Label>
                <Stack direction="horizontal" gap={1}>
                  <Form.Control type="file" onChange={handleFileChange} />
                  <Button
                    variant="danger"
                    onClick={() => setshowFileInput(false)}
                  >
                    X
                  </Button>
                </Stack>
              </Form.Group>
            )}
          </div>
          <Col>
            <Form.Control
              // type="text"
              as="textarea"
              rows={1}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your response..."
              onInput={(e) => {
                e.target.style.height = "auto"; // Reset height to auto
                const newHeight = Math.min(e.target.scrollHeight, 300); // Cap height at 300px
                e.target.style.height = `${newHeight}px`;
                e.target.style.overflowY =
                  e.target.scrollHeight > 300 ? "auto" : "hidden"; // Add scrollbar only if height exceeds 300px
              }}
              style={{
                width: "100%",
                resize: "none", // Prevent manual resizing
                overflow: "hidden", // Hide scrollbars initially
                maxHeight: "300px", // Enforce maximum height
                verticalAlign: "middle", // Align with buttons
                display: "flex",
                alignItems: "center", // Center content when height matches buttons
              }}
            />
          </Col>

          <Col xs="auto">
            <Button onClick={handleDoctorSend}>Send</Button>
            <Button
              variant="danger"
              onClick={() => {
                const url = `https://wa.me/${validateAndFormatPhoneNumber(
                  `${pPhone}`
                )}?text=${encodeURIComponent(
                  `Replied\n تم الرد على: \n\n${URL}`
                )}`;
                window.open(url, "_blank");
              }}
              style={{
                borderRadius: "30px",
                width: "40px",
                height: "40px",
                marginRight: "5px",
                marginLeft: "5px",
              }}
            >
              {BellFillIcon("", 15)}
            </Button>
          </Col>
          <p>{pPhone}</p>

          {capturedImage && (
            <div>
              <img
                src={capturedImage}
                alt="Captured"
                style={{ maxWidth: "200px", marginTop: "10px" }}
              />
            </div>
          )}
        </Row>
      ) : (
        <Stack direction="horizontal" gap={1} style={{ marginBottom: "10px" }}>
          <Button
            style={{ backgroundColor: BasicColor }}
            disabled={id ? false : true}
            onClick={() => setModalShow(true)}
          >
            {MusicPlayerIcon("", 20)}
          </Button>
          {/* <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={3}
              value={newMessage.note}
              onChange={(e) =>
                setNewMessage({ ...newMessage, note: e.target.value })
              }
            />
          </Form.Group> */}
          <Form.Group className="flex-grow-1 mb-0" style={{ margin: 0 }}>
            <Form.Control
              as="textarea"
              rows={1}
              value={newMessage.note}
              onChange={(e) =>
                setNewMessage({ ...newMessage, note: e.target.value })
              }
              onInput={(e) => {
                e.target.style.height = "auto"; // Reset height to auto
                const newHeight = Math.min(e.target.scrollHeight, 300); // Cap height at 300px
                e.target.style.height = `${newHeight}px`;
                e.target.style.overflowY =
                  e.target.scrollHeight > 300 ? "auto" : "hidden"; // Add scrollbar only if height exceeds 300px
              }}
              style={{
                width: "100%",
                resize: "none", // Prevent manual resizing
                overflow: "hidden", // Hide scrollbars initially
                maxHeight: "300px", // Enforce maximum height
                verticalAlign: "middle", // Align with buttons
                display: "flex",
                alignItems: "center", // Center content when height matches buttons
              }}
            />
          </Form.Group>
          <Button
            size="sm"
            style={{ backgroundColor: BasicColor }}
            onClick={handlePatientSend}
          >
            {SendIcon("", 20)}
          </Button>
        </Stack>
      )}

      {messageSent && (
        <div className="text-center mt-3">
          <span style={{ color: "green", fontSize: "1.5rem" }}>
            ✓ Message Sent
          </span>
        </div>
      )}

      <Modal show={modalShow} onHide={() => setModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("ComposeMsg")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t("FastingBs")}</Form.Label>
            <Form.Control
              type="number"
              value={toEnglishNumber(newMessage.fasting)}
              onChange={(e) =>
                setNewMessage({ ...newMessage, fasting: e.target.value })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("PpBs")}</Form.Label>
            <Form.Control
              type="number"
              value={toEnglishNumber(newMessage.postPrandial)}
              onChange={(e) =>
                setNewMessage({ ...newMessage, postPrandial: e.target.value })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("RandomBs")}</Form.Label>
            <Form.Control
              type="number"
              value={toEnglishNumber(newMessage.random)}
              onChange={(e) =>
                setNewMessage({ ...newMessage, random: e.target.value })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("Notes")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newMessage.note}
              onChange={(e) =>
                setNewMessage({ ...newMessage, note: e.target.value })
              }
            />
          </Form.Group>
          <div direction="horizontal" gap={1}>
            <Stack direction="horizontal">
              {!isIOS && (
                <ChatCameraCaptureButton
                  onCapture={(image) => {
                    setCapturedImage(image); // Set captured image
                    setFile(null); // Clear any selected file
                  }}
                />
              )}
              {!showFileInput && (
                <Button
                  style={{ backgroundColor: BasicColor }}
                  onClick={() => setshowFileInput(true)}
                >
                  {ImageIcon("", 20)}
                </Button>
              )}
            </Stack>
            {showFileInput && (
              <Form.Group className="mb-3">
                <Form.Label>Attach File</Form.Label>
                <Stack direction="horizontal" gap={1}>
                  <Form.Control type="file" onChange={handleFileChange} />
                  <Button
                    variant="danger"
                    onClick={() => setshowFileInput(false)}
                  >
                    X
                  </Button>
                </Stack>
              </Form.Group>
            )}
          </div>

          {capturedImage && (
            <div>
              <img
                src={capturedImage}
                alt="Captured"
                style={{ maxWidth: "200px", marginTop: "10px" }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handlePatientSend}>Send</Button>
        </Modal.Footer>
      </Modal>
      {isLoading && <LoadingDots />}
    </Container>
  );
};

export default ChatApp;
